import { SearchActions } from './actions';
import { SearchActionTypes, SearchState } from './types';
import { SearchFilterTypes, SearchGroup } from '@fullstory/search-model';
import { EventType, UserType, equalType } from '@fullstory/search-types';
import { appendDependents, isDefaultSearch } from './utils';
import { Duration } from '@fullstory/proto/google/protobuf';

const initialState: SearchState = {
  // some things can accidentally trigger a search before we have loaded the correct segment time range
  // if this happens, just issue a "today" search, and while wasteful it will still be better than defaulting to empty all time
  // TODO(clay): pipe through understanding across the product for a null search model before everything is loaded
  timeRange: {
    Start: 'NOW/DAY',
    End: 'NOW/DAY+1DAY',
  },
  uiModel: {
    name: '',
    description: '',
    events: [],
    notEvents: [],
    users: [],
    inEventOrder: false,
    inSameSession: false,
    completeSessions: false,
    platform: null,
    withinTime: Duration.zero,
    eventThresholds: [],
    excludeEventThresholds: [],
  },
  searchModel: {
    name: '',
    description: '',
    events: [],
    notEvents: [],
    users: [],
    inEventOrder: false,
    inSameSession: false,
    completeSessions: false,
    platform: null,
    withinTime: Duration.zero,
    eventThresholds: [],
    excludeEventThresholds: [],
  },
  pendingSuggestion: null,
};

export function searchReducer(
  state: SearchState = initialState,
  action: SearchActions,
): SearchState {
  switch (action.type) {
    case SearchActionTypes.SetTimeRange:
      return {
        ...state,
        timeRange: action.data,
      };
    case SearchActionTypes.AppendDependents:
      if (action.mode !== 'segment') return state;
      switch (action.group) {
        case SearchFilterTypes.Events:
          return appendDependents(state, 'events', action.data);
        case SearchFilterTypes.NotEvents:
          return appendDependents(state, 'notEvents', action.data);
        case SearchFilterTypes.Users:
          return appendDependents(state, 'users', action.data);
        default:
          return state;
      }
    case SearchActionTypes.AddRow: {
      if (action.mode !== 'segment') return state;
      const replaceModel = isDefaultSearch(state, action.group);
      switch (action.group) {
        case SearchFilterTypes.Events: {
          const events = replaceModel
            ? [action.data as SearchGroup<EventType>]
            : state.uiModel.events.concat(action.data as SearchGroup<EventType>);
          return {
            ...state,
            uiModel: {
              ...state.uiModel,
              events,
            },
            searchModel: {
              ...state.searchModel,
              events,
            },
          };
        }
        case SearchFilterTypes.NotEvents: {
          const notEvents = replaceModel
            ? [action.data as SearchGroup<EventType>]
            : state.uiModel.notEvents.concat(action.data as SearchGroup<EventType>);
          return {
            ...state,
            uiModel: {
              ...state.uiModel,
              notEvents,
            },
            searchModel: {
              ...state.searchModel,
              notEvents,
            },
          };
        }
        case SearchFilterTypes.Users: {
          const users = replaceModel
            ? [action.data as SearchGroup<UserType>]
            : state.uiModel.users.concat(action.data as SearchGroup<UserType>);
          return {
            ...state,
            uiModel: {
              ...state.uiModel,
              users,
            },
            searchModel: {
              ...state.searchModel,
              users,
            },
          };
        }
        default:
          return state;
      }
    }
    case SearchActionTypes.ReplaceOrAddUserRow: {
      if (action.mode !== 'segment') return state;
      const index = state.uiModel.users.findIndex(type =>
        equalType(type.primary, action.data.primary),
      );
      // replace row if exists
      if (index >= 0) {
        const _state: SearchState = {
          ...state,
          uiModel: {
            ...state.uiModel,
            users: [
              ...state.uiModel.users.slice(0, index),
              {
                ...action.data,
              },
              ...state.uiModel.users.slice(index + 1),
            ],
          },
          searchModel: {
            ...state.searchModel,
            // Replace searchModel users with new uiModel users because row not guaranteed to exist in searchModel
            users: [
              ...state.uiModel.users.slice(0, index),
              {
                ...action.data,
              },
              ...state.uiModel.users.slice(index + 1),
            ],
          },
        };
        return _state;
      } else {
        // else append row
        const users = isDefaultSearch(state, SearchFilterTypes.Users)
          ? [action.data as SearchGroup<UserType>]
          : state.uiModel.users.concat(action.data as SearchGroup<UserType>);
        return {
          ...state,
          uiModel: {
            ...state.uiModel,
            users,
          },
          searchModel: {
            ...state.searchModel,
            users,
          },
        };
      }
    }
    case SearchActionTypes.SetInSameSession:
      return {
        ...state,
        uiModel: {
          ...state.uiModel,
          inSameSession: action.inSameSession,
        },
      };
    case SearchActionTypes.SetPlatform:
      return {
        ...state,
        uiModel: {
          ...state.uiModel,
          platform: action.platform,
        },
      };
    case SearchActionTypes.SetModel: {
      const _state: SearchState = {
        ...state,
        uiModel: action.model,
      };

      if (action.syncSearchModel) {
        _state.searchModel = _state.uiModel;
      }

      return _state;
    }
    case SearchActionTypes.SetPendingSuggestion: {
      const _state: SearchState = {
        ...state,
        pendingSuggestion: {
          suggestion: action.suggestion,
          mode: action.mode,
        },
      };

      return _state;
    }
    case SearchActionTypes.ClearPendingSuggestion: {
      const _state: SearchState = {
        ...state,
        pendingSuggestion: null,
      };

      return _state;
    }
    default:
      return state;
  }
}
